import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import {
  Plus, Trash2, Save, Users, Lock, Hash, ChevronDown, ChevronUp, Eye, EyeOff,
  DollarSign, TrendingUp, X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ServersTab = ({ tables }) => {
  const [servers, setServers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newServer, setNewServer] = useState({ name: '', pin: '', assigned_tables: [] });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showPin, setShowPin] = useState({});
  const [tips, setTips] = useState([]);

  const fetchServers = async () => {
    try {
      const [sRes, tRes] = await Promise.all([
        axios.get(`${API}/admin/servers`),
        axios.get(`${API}/admin/server-tips`),
      ]);
      setServers(sRes.data);
      setTips(tRes.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchServers(); }, []);

  const createServer = async () => {
    if (!newServer.name || !newServer.pin) return;
    try {
      await axios.post(`${API}/admin/servers`, newServer);
      setNewServer({ name: '', pin: '', assigned_tables: [] });
      setShowNew(false);
      fetchServers();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const updateServer = async (id) => {
    try {
      await axios.patch(`${API}/admin/servers/${id}`, editData);
      setEditingId(null);
      setEditData({});
      fetchServers();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const deleteServer = async (id) => {
    if (!window.confirm('Supprimer ce serveur ?')) return;
    try {
      await axios.delete(`${API}/admin/servers/${id}`);
      fetchServers();
    } catch (e) { console.error(e); }
  };

  const toggleTable = (tableId, current) => {
    if (current.includes(tableId)) {
      return current.filter(t => t !== tableId);
    }
    return [...current, tableId];
  };

  const formatPrice = (v) => `${(v || 0).toLocaleString()} THB`;
  const allTableIds = tables.filter(t => t.is_active !== false).map(t => String(t.number || t.id));

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#2D241D]" data-testid="servers-title">Serveurs ({servers.length})</h3>
          <p className="text-xs text-[#6E5E52]">Gerez vos serveurs, PINs et tables assignees</p>
        </div>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid="add-server-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau serveur
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white rounded-3xl admin-theme">
            <DialogHeader><DialogTitle className="text-[#2D241D]">Nouveau serveur</DialogTitle></DialogHeader>
            <div className="space-y-4 p-4">
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Nom</label>
                <Input value={newServer.name}
                  onChange={(e) => setNewServer({ ...newServer, name: e.target.value })}
                  placeholder="ex: Marie" className="rounded-xl border-[#E5DCD3]"
                  data-testid="new-server-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">PIN (4-6 chiffres)</label>
                <Input type="number" value={newServer.pin}
                  onChange={(e) => setNewServer({ ...newServer, pin: e.target.value })}
                  placeholder="1234" className="rounded-xl border-[#E5DCD3]"
                  data-testid="new-server-pin" maxLength={6} />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-2 block">Tables assignees</label>
                <div className="flex flex-wrap gap-2">
                  {allTableIds.map(tid => (
                    <button key={tid} onClick={() => setNewServer({ ...newServer, assigned_tables: toggleTable(tid, newServer.assigned_tables) })}
                      className={`w-10 h-10 rounded-lg text-sm font-bold transition-colors ${newServer.assigned_tables.includes(tid) ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#2D241D] border border-[#E5DCD3]'}`}
                      data-testid={`new-server-table-${tid}`}>
                      {tid}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={createServer} disabled={!newServer.name || !newServer.pin}
                className="w-full bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid="save-server-btn">
                <Save className="w-4 h-4 mr-2" /> Creer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tips summary */}
      {tips.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <h4 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#D4A853]" /> Pourboires du jour
          </h4>
          <div className="space-y-2">
            {tips.map((t, idx) => (
              <div key={idx} className="flex items-center justify-between bg-[#F3EDE5] rounded-xl px-3 py-2" data-testid={`server-tip-${idx}`}>
                <div>
                  <span className="text-sm font-medium text-[#2D241D]">{t.server_name}</span>
                  <span className="text-xs text-[#6E5E52] ml-2">{t.orders} cmd | CA: {formatPrice(t.revenue)}</span>
                </div>
                <span className="text-sm font-bold text-[#2D6A4F]">{formatPrice(t.tips)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Server cards */}
      <div className="space-y-3">
        {servers.map(srv => {
          const isEditing = editingId === srv.id;
          return (
            <div key={srv.id} className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid={`server-card-${srv.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0A422D]/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#0A422D]" />
                  </div>
                  <div>
                    {isEditing ? (
                      <Input value={editData.name ?? srv.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        className="h-7 text-sm rounded-lg border-[#E5DCD3] w-32" />
                    ) : (
                      <p className="font-bold text-[#2D241D]">{srv.name}</p>
                    )}
                    <div className="flex items-center gap-1 text-xs text-[#6E5E52]">
                      <Lock className="w-3 h-3" />
                      {showPin[srv.id] ? srv.pin : '****'}
                      <button onClick={() => setShowPin(prev => ({ ...prev, [srv.id]: !prev[srv.id] }))} className="ml-1">
                        {showPin[srv.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isEditing ? (
                    <>
                      <Button size="sm" onClick={() => updateServer(srv.id)}
                        className="bg-[#0A422D] text-white rounded-lg h-7 text-xs px-2" data-testid={`save-edit-${srv.id}`}>
                        <Save className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditData({}); }}
                        className="text-[#6E5E52] h-7 px-2"><X className="w-3 h-3" /></Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => {
                        setEditingId(srv.id);
                        setEditData({ name: srv.name, assigned_tables: srv.assigned_tables });
                      }}
                        className="text-[#6E5E52] h-7 px-2" data-testid={`edit-server-${srv.id}`}>
                        Modifier
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteServer(srv.id)}
                        className="text-[#E63946] h-7 px-2" data-testid={`delete-server-${srv.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Tables */}
              <div>
                <p className="text-xs font-medium text-[#6E5E52] mb-2">Tables: {srv.assigned_tables?.length || 0}</p>
                <div className="flex flex-wrap gap-1.5">
                  {allTableIds.map(tid => {
                    const assigned = isEditing
                      ? (editData.assigned_tables || srv.assigned_tables || []).includes(tid)
                      : (srv.assigned_tables || []).includes(tid);
                    return (
                      <button key={tid}
                        onClick={() => isEditing && setEditData({
                          ...editData,
                          assigned_tables: toggleTable(tid, editData.assigned_tables || srv.assigned_tables || [])
                        })}
                        disabled={!isEditing}
                        className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${assigned ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#6E5E52]'} ${isEditing ? 'cursor-pointer' : 'cursor-default'}`}
                        data-testid={`table-assign-${srv.id}-${tid}`}>
                        {tid}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
        {servers.length === 0 && (
          <div className="text-center py-12 text-[#6E5E52] bg-white rounded-2xl border border-[#E5DCD3]">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucun serveur configure</p>
            <p className="text-xs mt-1">Creez un serveur pour activer l'acces POS</p>
          </div>
        )}
      </div>
    </div>
  );
};
